import { ERROR_CODES, isApiErrorBody, type ApiErrorBody } from '@asyagrup/group-hub-contracts';
import { sirayaSok } from './sekme-kilidi';

const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly statusCode: number,
    readonly details?: ApiErrorBody['error']['details'],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The access token lives in memory only.
 *
 * asya.click keeps both the access and the 30-day refresh token in
 * localStorage, which turns any XSS into a full account takeover that survives
 * a browser restart. The refresh token here is an httpOnly cookie the script
 * cannot read, and the access token dies with the tab.
 */
let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

/**
 * The three things a refresh attempt can mean.
 *
 * It used to return a boolean, and that was the whole of DST-00042: `false`
 * meant "the server says this session is over" AND "I could not reach the
 * server", and the caller acted on the first reading. Every release recreates
 * Caddy — the only listener on 443 — so the second reading is the common one:
 * fifteen releases shipped on the day this was filed, and on that day there
 * were ZERO failed logins while sessions were dropped all afternoon. Each
 * abandoned session ended on a refresh token still live in the database,
 * `consumed_at` and `revoked_at` both NULL. That row is the proof: the request
 * never arrived, and the client invented the logout by itself.
 *
 * `unauthenticated` is now the narrow case it always should have been — the
 * server answered, about the session, and said no.
 */
export type RefreshOutcome = 'ok' | 'unauthenticated' | 'unavailable';

/**
 * The only two statuses that are an answer ABOUT THE SESSION.
 *
 * Everything else the transport can produce — 429 from a rate limiter, 502/503
 * from a proxy with no upstream, 500 from a handler that threw, 404 from a
 * route that has not finished deploying — says something about the server, and
 * nothing whatsoever about whether this person is still signed in. Treating
 * them as a logout is answering a question that was never asked.
 */
function endsSession(status: number): boolean {
  return status === 401 || status === 403;
}

/**
 * Does this thrown value PROVE the session is over?
 *
 * Exported because the boot path in `session-context` must make the identical
 * judgement, and two copies of a rule this consequential would drift — one of
 * them silently, in the direction of signing people out.
 */
export function isSessionEnded(error: unknown): boolean {
  return error instanceof ApiError && endsSession(error.statusCode);
}

/**
 * One refresh attempt. Exactly one — `/auth/refresh` may never be retried.
 *
 * The obvious shape here is a backoff ladder: the gap during a release is
 * seconds, so waiting it out looks strictly kinder than telling the caller the
 * server is unreachable. It is not, because this endpoint is single-use and NOT
 * idempotent. `AuthService.refresh` commits `consumedAt` BEFORE it writes its
 * reply (`auth.service.ts:271`), and a token presented once it is spent is read
 * as THEFT with no tolerance window at all (`:252`): the whole family is
 * revoked, every device is signed out, and a `critical` security event is filed
 * about an attack that never happened.
 *
 * A retry is safe only if the request certainly never reached the server, and
 * the browser cannot know that. `fetch` rejects with the same opaque TypeError
 * whether the connection was refused before a byte left — where a retry would
 * be harmless — or reset while the reply was coming back, which is precisely
 * what a Caddy swap mid-release produces. Retrying on that guess would
 * manufacture, at scale, a logout worse than the one this change exists to
 * prevent: the user is not merely signed out, they are told their sessions were
 * closed for security.
 *
 * So the ladder is gone and the outcome is reported honestly. The session
 * survives either way; a later attempt costs the user a click, not a password.
 *
 * The residual hazard is older than this file and NOT fixed here: if the reply
 * is lost after `consumedAt` is committed, the NEXT refresh — whenever it
 * happens — presents a spent token and is read as theft. That is the likeliest
 * reading of the fifteen `reuse_detected` events behind DST-00032. Closing it
 * needs a grace window on the server, which is an authentication decision and
 * not one to take in passing.
 */
async function runRefresh(): Promise<RefreshOutcome> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'same-origin',
    });
  } catch {
    // Nothing answered. Whether the token was spent is unknowable from here,
    // so this reports and does not re-present it.
    return 'unavailable';
  }

  if (endsSession(res.status)) return 'unauthenticated';
  if (!res.ok) return 'unavailable';

  // A 200 is not yet proof of a refresh. A deleted asset URL falling through
  // an SPA rewrite answers 200 with index.html, and a body that does not
  // parse — or parses without a token — is a broken deploy, not a logout.
  let body: { accessToken?: string };
  try {
    body = (await res.json()) as { accessToken?: string };
  } catch {
    return 'unavailable';
  }
  if (!body.accessToken) return 'unavailable';

  accessToken = body.accessToken;
  return 'ok';
}

/**
 * Single-flight refresh, ACROSS TABS.
 *
 * Mandatory with rotating refresh tokens: if three requests 401 at once and
 * each triggers its own refresh, the second and third present an
 * already-consumed token, the server reads that as theft, and the user is
 * logged out for being fast. One in-flight promise, shared.
 *
 * The promise below only shares it within ONE tab, and that was half the fix.
 * The refresh token is a browser-wide cookie, so two tabs racing read the same
 * value and the second one is the theft the server is watching for — it then
 * revokes the whole family and both tabs are thrown out. Measured in
 * production: 15 `reuse_detected` on one account, the reporter concluding the
 * browser or their phone was to blame (DST-00032).
 *
 * `sirayaSok` puts the fetch behind a Web Lock, which every tab of this origin
 * shares and which is released automatically if a tab dies. The in-tab promise
 * stays: it keeps three simultaneous 401s in one tab from queueing three turns
 * on that lock.
 *
 * The retry ladder lives INSIDE this promise, which is what keeps the two
 * mechanisms from fighting: concurrent callers share one ladder rather than
 * starting three of their own against the same cookie.
 */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

async function refreshAccessToken(): Promise<RefreshOutcome> {
  refreshInFlight ??= sirayaSok('grouphub-auth-refresh', async () => {
    try {
      return await runRefresh();
    } finally {
      // Cleared on the next tick so concurrent callers all observe the result.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  });

  return refreshInFlight;
}

/**
 * What the caller is told when the refresh could not be carried out.
 *
 * Deliberately NOT the original 401: that response says "log in again", which
 * is precisely the false claim being removed. Every screen renders this through
 * `errorMessage`, so the sentence is the one the user reads — and it says the
 * session is still open, because it is.
 */
function unreachableError(): ApiError {
  return new ApiError(
    ERROR_CODES.INTERNAL,
    'Sunucuya ulaşılamadı. Oturumunuz açık; bağlantı gelince tekrar deneyin.',
    503,
  );
}

/**
 * Açılışta oturumu geri yükler — AYNI kilidin içinden.
 *
 * `refreshAccessToken` yalnız 401 sonrası yenilemeyi kilitliyordu. Açılış yolu
 * ise `session-context` içinden doğrudan `api.post('/auth/refresh')` çağırıyor
 * ve o çağrı kilide hiç uğramıyordu — yani sekmeler arası korumanın DIŞINDA
 * kalan bir yenileme daha vardı.
 *
 * Üstelik daha sık tetiklenen yol bu: üç sekmeyi birlikte açmak ya da tarayıcı
 * oturumunu geri yüklemek, üç açılış yenilemesini aynı anda başlatır. Üçü de
 * aynı çerezi sunar, sunucu ikinciyi hırsızlık sayar ve ailenin tamamını iptal
 * eder — kullanıcı hiçbir şey yapmadan bütün sekmelerden atılır. v0.66.7 401
 * yolunu kapatmıştı; bu, aynı işin yarım kalan yarısı.
 *
 * `skipRefresh` şart, süs değil: Web Lock yeniden girişli DEĞİL. İçerideki
 * istek 401 alıp `refreshAccessToken`'a saparsa aynı kilidi ikinci kez almaya
 * çalışır ve sekme kilitlenir — kendi kendini bekleyerek.
 */
export async function oturumuGeriYukle<T>(signal?: AbortSignal): Promise<T> {
  return sirayaSok('grouphub-auth-refresh', () =>
    apiRequest<T>('/auth/refresh', {
      method: 'POST',
      skipRefresh: true,
      ...(signal ? { signal } : {}),
    }),
  );
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Skip the 401 -> refresh -> retry dance (used by auth endpoints). */
  skipRefresh?: boolean;
  signal?: AbortSignal;
}

async function toApiError(res: Response): Promise<ApiError> {
  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* non-JSON error body */
  }
  if (isApiErrorBody(payload)) {
    const e = payload.error;
    return new ApiError(e.code, e.message, e.statusCode, e.details, e.requestId);
  }
  return new ApiError(ERROR_CODES.INTERNAL, 'Sunucuya ulaşılamadı.', res.status);
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, skipRefresh = false, signal } = options;

  const send = async (): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'same-origin',
      signal,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  let res = await send();

  if (res.status === 401 && !skipRefresh) {
    const outcome = await refreshAccessToken();
    if (outcome === 'ok') {
      res = await send();
    } else if (outcome === 'unauthenticated') {
      accessToken = null;
      onSessionLost?.();
      throw await toApiError(res);
    } else {
      // Unreachable. The token stays exactly where it is and `onSessionLost`
      // is not fired: nothing has been learned about this session, so ending
      // it would be a guess — and the measured evidence says the guess is
      // wrong far more often than it is right.
      throw unreachableError();
    }
  }

  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Sends a file, and reads one back.
 *
 * Separate from `apiRequest` because both differ from it in the same place:
 * the body must NOT be JSON-serialised and the response must NOT be
 * JSON-parsed. They still share the 401-refresh-retry dance, so a session that
 * expires mid-upload recovers rather than losing the file.
 */
async function apiBinary(
  path: string,
  init: { method: 'GET' | 'POST'; body?: FormData },
): Promise<Response> {
  const send = async (): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method: init.method,
      credentials: 'same-origin',
      // Content-Type is deliberately unset for FormData: the browser adds it
      // WITH the multipart boundary, and setting it by hand loses the boundary
      // and the server sees a body it cannot parse.
      headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      ...(init.body ? { body: init.body } : {}),
    });

  let res = await send();
  if (res.status === 401) {
    const outcome = await refreshAccessToken();
    if (outcome === 'ok') {
      res = await send();
    } else if (outcome === 'unauthenticated') {
      accessToken = null;
      onSessionLost?.();
      throw await toApiError(res);
    } else {
      // Same reasoning as `apiRequest`, and it matters more here: losing a
      // session mid-upload also loses the file the user just chose.
      throw unreachableError();
    }
  }
  if (!res.ok) throw await toApiError(res);
  return res;
}

export const api = {
  postForm: async <T>(path: string, form: FormData): Promise<T> => {
    const res = await apiBinary(path, { method: 'POST', body: form });
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  },
  getBlob: async (path: string): Promise<Blob> => {
    const res = await apiBinary(path, { method: 'GET' });
    return res.blob();
  },
  get: <T>(path: string, signal?: AbortSignal) => apiRequest<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

/**
 * Turns any thrown value into something safe to render.
 *
 * Every error the user reads comes through here — 52 screens call it — so the
 * one thing it must never do is hand the browser's own wording to the screen.
 * That is what it did for the case below.
 */
export function errorMessage(error: unknown, fallback = 'Beklenmeyen bir hata oluştu.'): string {
  // The server's own message, already Turkish and already written for the
  // person reading it.
  if (error instanceof ApiError) return error.message;

  /*
   * `fetch` rejects with a TypeError — and only with a TypeError — when no
   * response arrived: no network, DNS gone, the origin blocked, or the API
   * restarting mid-deploy, which on this system happens on every release.
   *
   * Note what it does NOT tell us: whether the request was heard. A connection
   * reset while the reply was coming back rejects exactly like a connection
   * that was refused. Nothing may be concluded here about server state — see
   * `runRefresh`, where that distinction is the difference between a retry and
   * a forged theft alarm.
   *
   * The message on that TypeError is written by the browser, in English, and
   * differs between them: "Failed to fetch" in Chrome, "Load failed" in Safari,
   * "NetworkError when attempting to fetch resource." in Firefox. All three
   * used to be printed verbatim, in red, in the middle of a screen where every
   * other word is Turkish — and none of them says the part worth knowing: the
   * data is fine, the connection is not, and trying again is reasonable.
   *
   * Matched on the TYPE rather than on the text. Matching the text would mean
   * keeping a list of three browsers' phrasings and losing to the fourth.
   */
  if (error instanceof TypeError) {
    return 'Sunucuya ulaşılamadı. Bağlantınızı kontrol edip tekrar deneyin.';
  }

  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * The per-field reasons behind a 400, keyed by the field they belong to.
 *
 * `HttpExceptionFilter` has always put them on the wire — a ZodError becomes
 * `details.issues[]` with a `path` and a Turkish `message` — and until now
 * nothing on this side read them. So every rejected form showed the envelope's
 * summary sentence, "Gönderilen bilgiler geçersiz.", and the sentence that
 * actually says WHICH field and WHY was thrown away in the browser.
 *
 * Only the first issue per path survives: Zod reports the earliest failure per
 * field, later ones are consequences of it, and a field can show one line.
 *
 * Nested paths keep their dots (`lines.0.amount`) so a caller can match either
 * the whole path or its head. Issues with an empty path belong to the object
 * itself — a cross-field `.refine()` — and are keyed by `''`; `formErrorOf`
 * below is how a caller reaches them without inventing a second convention.
 */
export function fieldIssues(error: unknown): Map<string, string> {
  const out = new Map<string, string>();
  if (!(error instanceof ApiError)) return out;
  for (const issue of error.details?.issues ?? []) {
    if (!out.has(issue.path)) out.set(issue.path, issue.message);
  }
  return out;
}

/**
 * The message for one field, or `undefined` when the server did not blame it.
 *
 * Returns `undefined` rather than `null` because that is what `Field`'s
 * optional `error` prop takes; a `null` would render an empty red line.
 */
export function fieldErrorOf(error: unknown, path: string): string | undefined {
  return fieldIssues(error).get(path);
}

/**
 * The part of a validation failure that belongs to no single field.
 *
 * A cross-field rule — "son ödeme tarihi tahakkuk tarihinden önce olamaz" — is
 * reported by Zod against the object or against one arbitrary member of the
 * pair. Showing it only next to that one field hides it from the person who
 * typed the OTHER one, so it is surfaced at the top of the form as well.
 *
 * Falls back to the envelope's own message so the caller always has a sentence
 * to show; returns `undefined` when the error is not a validation failure at
 * all, which is the caller's signal to fall through to its own handling.
 */
export function formErrorOf(error: unknown, ownedPaths: readonly string[]): string | undefined {
  if (!(error instanceof ApiError) || error.code !== ERROR_CODES.VALIDATION) return undefined;
  const issues = fieldIssues(error);
  const unclaimed = [...issues.entries()]
    .filter(([path]) => !ownedPaths.includes(path))
    .map(([, message]) => message);
  if (unclaimed.length > 0) return unclaimed.join(' ');
  // Every issue is already shown beside its own field; repeating them at the
  // top would say the same thing twice.
  return issues.size > 0 ? undefined : error.message;
}
